import axios from "../../../../api/axios";
import { activateExpeditionRoute, deactivateExpeditionRoute, getActiveExpeditionRoute, recoverExpeditionRoute, startExpeditionRoute } from "../../../../api/routes";


/* All times are unix timestamps in seconds */
const expeditionDefaultState = {
    expeditionInited: false,
    expeditionActive: false,
    expeditionStartTime: 0,
    expeditionTimeAllowed: 0, // in seconds, 0 = unlimited
    expeditionTargetEndTime: 0, // unix timestamp in seconds
    expeditionCompletedTime: 0, // unix timestamp in seconds

    allTeamsChatUnlocked: false,
    allTeamsChatUnlockedFor: 0, // time in seconds (needed for alerts)
    allTeamsChatUnlockedUntil: 0, // unix timestamp in seconds

    teamStates: [],
}

const expeditionDefaultTeamState = {
    teamName: "Team 1",   // This is only here for reference (if we are looking at the data manually), the team is identified by its index
    teamIndex: 0,
    totalXp: 0,

    journeyStage: 0,
    stagePercentage: 0,
    currentWaypoint: 0,
    waypointPercentage: 0,

    inventory: [
        /* {
            itemName: "Item",   // This is only here for reference (if we are looking at the data manually), the item is identified by its index
            itemIndex: 0,
        } */
    ],

    activeChallenge: null,
    activeChallengeStartTime: 0,
    /* {
        challengeName: "Challenge",  // This is only here for reference (if we are looking at the data manually), the challenge is identified by its index
        challengeIndex: 0,
        startedTime: 0,
        completedTime: 0,
        successful: true,
    }, */
    endedChallenges: [
        /* {
            challengeName: "Challenge",  // This is only here for reference (if we are looking at the data manually), the challenge is identified by its index
            challengeIndex: 0,
            startedTime: 0,
            completedTime: 0,
            successful: true,
        } */
    ],

    compassLockedFor: 0, // time in seconds (needed for alerts)
    compassLockedUntil: 0, // unix timestamp in seconds

    chatUnlockedFor: 0, // time in seconds (needed for alerts)
    chatUnlockedUntil: 0, // unix timestamp in seconds

    expeditionCompleted: false,
    expeditionCompletedTime: 0,
}

const getInitialExpeditionState = (customisationData, campaignID, featureID, assetBaseUrl, defaultAssetBaseUrl) => {
    const newExpeditionState = JSON.parse(JSON.stringify(expeditionDefaultState));

    newExpeditionState.campaignID = campaignID;
    newExpeditionState.featureID = featureID;
    newExpeditionState.assetBaseUrl = assetBaseUrl || defaultAssetBaseUrl || "";
    newExpeditionState.defaultAssetBaseUrl = defaultAssetBaseUrl || "";

    for (let i = 0; i < customisationData.setup.teams.teams.length; i++) {
        const newTeamState = JSON.parse(JSON.stringify(expeditionDefaultTeamState));
        newTeamState.teamName = customisationData.setup.teams.teams[i].name || ("Team " + (i + 1));
        newTeamState.teamIndex = i;
        // Initialise inventory with starting items
        const inventoryData = customisationData.setup.inventory.items;
        newTeamState.inventory = inventoryData.map((item, index) => ({
            itemName: item.name,
            itemIndex: index,
        }));
        newExpeditionState.teamStates.push(newTeamState);
    }
    newExpeditionState.expeditionInited = true;
    return newExpeditionState;
}

class ExpeditionLiveState {
    updateListeners = [];
    expeditionMainState = null;
    socket = null;

    registerUpdateListener = (callback) => {
        if (callback && !this.updateListeners.includes(callback)) {
            this.updateListeners.push(callback);
        }
    }
    unregisterUpdateListener = (callback) => {
        this.updateListeners = this.updateListeners.filter((cb) => cb !== callback);
    }

    initializeSocket = (socket) => {
        console.log('--- ExpeditionLiveState, initializeSocket', socket);
        this.socket = socket;
        // All updates to stream use the same socket message, the data.type field is used to differentiate the type of update
        this.socket.on('expeditionUpdateState', this.onStateUpdate);
        // this.socket.on('sendMessageToTeam', this.onStateUpdate);

        // Listeners for app events that don't send the full state
        /* this.socket.on('waypointReached', this.onWaypointReached);
        this.socket.on('checkpointReached', this.onCheckpointReached);
        this.socket.on('challengeStarted', this.onChallengeStarted);
        this.socket.on('challengeComplete', this.onChallengeCompleted);
        this.socket.on('updateTeamLocation', this.onUpdateTeamLocation); */

        // Need to add checkpointReached, but there is an issue with the backend not sending the data correctly yet
    }
    cancelSocketListeners = () => {
        if (this.socket) {
            this.socket.off('expeditionUpdateState', this.onStateUpdate);
            // this.socket.off('sendMessageToTeam', this.onStateUpdate);
            /* this.socket.off('waypointReached', this.onWaypointReached);
            this.socket.off('checkpointReached', this.onCheckpointReached);
            this.socket.off('challengeStarted', this.onChallengeStarted);
            this.socket.off('challengeComplete', this.onChallengeCompleted);
            this.socket.off('updateTeamLocation', this.onUpdateTeamLocation);
            console.log('--- ExpeditionLiveState, cancelSocketListeners'); */
            this.socket = null;
        }
    }
    registerAsPrimaryStream = (streamId) => {
        console.log('--- ExpeditionLiveState, registerAsPrimaryStream');
        if (this.socket) {
            console.log('--- Emitting expeditionSetPrimaryStream for streamID:', streamId);
            this.socket.emit('expeditionSetPrimaryStream', { streamID: streamId });
        }
    }
    registerForLiveUpdates = (streamId) => {
        console.log('--- ExpeditionLiveState, registerForLiveUpdates');
        if (this.socket) {
            console.log('--- Emitting expeditionAddStream for streamID:', streamId);
            this.socket.emit('expeditionAddStream', { streamID: streamId });
        }
    }

    informUpdateListenersNow = (data = null) => {
        if (this.updateListeners.length > 0) {
            this.updateListeners.forEach((callback) => {
                if (callback) callback(this.expeditionMainState, data);
            });
        }
    }

    onStateUpdate = (data) => {
        console.log('--- ExpeditionLiveState, onStateUpdate', data);
        if (data && (data.type === 'heartbeat' || data.type === 'expeditionStarted')) {
            this.expeditionMainState = data.data;
            this.informUpdateListenersNow(data);
        }
        switch (data?.type) {
            case 'waypointReached':
                this.onWaypointReached(data);
                break;
            case 'checkpointReached':
                this.onCheckpointReached(data);
                break;
            case 'challengeStarted':
                this.onChallengeStarted(data);
                break;
            case 'challengeComplete':
                this.onChallengeCompleted(data);
                break;
            case 'updateTeamLocation':
                this.onUpdateTeamLocation(data);
                break;
            case 'silentlySetJourneyStage':
                this.onSilentlySetJourneyStage(data);
                break;
            default:
                // console.log('--- ExpeditionLiveState, onStateUpdate, unknown type:', data?.type);
                this.informUpdateListenersNow(data);
                break;
        }
    }

    onWaypointReached = (data) => {
        // data = {teamIndex, stageIndex, waypointIndex}
        console.log('--- ExpeditionLiveState, onWaypointReached', data);
        const sentData = data?.data ?? data;
        // Update local state immediately
        if (this.expeditionMainState && this.expeditionMainState.teamStates && this.expeditionMainState.teamStates[sentData.teamIndex]) {
            this.expeditionMainState.teamStates[sentData.teamIndex].journeyStage = sentData.stageIndex;
            this.expeditionMainState.teamStates[sentData.teamIndex].currentWaypoint = sentData.waypointIndex;
        }
        this.informUpdateListenersNow(data);
    }

    onCheckpointReached = (data) => {
        // data = {teamIndex, stageIndex, checkpointIndex}
        console.log('--- ExpeditionLiveState, onCheckpointReached', data);
        const sentData = data?.data ?? data;
        // Update local state immediately
        if (this.expeditionMainState && this.expeditionMainState.teamStates && this.expeditionMainState.teamStates[sentData.teamIndex]) {
            this.expeditionMainState.teamStates[sentData.teamIndex].journeyStage = sentData.stageIndex;
            this.expeditionMainState.teamStates[sentData.teamIndex].currentWaypoint = sentData?.waypointIndex;
            this.expeditionMainState.teamStates[sentData.teamIndex].activeChallenge = sentData.challengeIndex;
        }
        this.informUpdateListenersNow(data);
    }

    onSilentlySetJourneyStage = (data) => {
        // data = {teamIndex, stageIndex, waypointIndex, challengeIndex}
        console.log('--- ExpeditionLiveState, onSilentlySetJourneyStage', data);
        const sentData = data?.data ?? data;
        // Update local state immediately
        if (this.expeditionMainState && this.expeditionMainState.teamStates && this.expeditionMainState.teamStates[sentData.teamIndex]) {
            this.expeditionMainState.teamStates[sentData.teamIndex].journeyStage = sentData.stageIndex;
            this.expeditionMainState.teamStates[sentData.teamIndex].currentWaypoint = sentData.waypointIndex;
            this.expeditionMainState.teamStates[sentData.teamIndex].activeChallenge = sentData.challengeIndex;
            this.expeditionMainState.teamStates[sentData.teamIndex].activeChallengeStartTime = 0;
            // this.expeditionMainState.teamStates[sentData.teamIndex].stagePercentage = 0;
        }
        this.informUpdateListenersNow(data);
    }

    onChallengeStarted = (data) => {
        // data = {teamIndex, challengeIndex, startedTime}
        console.log('--- ExpeditionLiveState, onChallengeStarted', data);
        const sentData = data?.data ?? data;
        // Update local state immediately
        if (this.expeditionMainState && this.expeditionMainState.teamStates && this.expeditionMainState.teamStates[sentData.teamIndex]) {
            this.expeditionMainState.teamStates[sentData.teamIndex].activeChallenge = sentData.challengeIndex;
            this.expeditionMainState.teamStates[sentData.teamIndex].activeChallengeStartTime = sentData.startTime;
        }
        this.informUpdateListenersNow(data);
    }

    onChallengeCompleted = (data) => {
        // data = {teamIndex, challengeIndex, completedTime, successful, moveOnToJourneyStage, isFinalChallenge, xpAwarded}
        console.log('--- ExpeditionLiveState, onChallengeCompleted', data);
        const sentData = data?.data ?? data;
        // Update local state immediately
        if (this.expeditionMainState && this.expeditionMainState.teamStates && this.expeditionMainState.teamStates[sentData.teamIndex]) {
            if (this.expeditionMainState.teamStates[sentData.teamIndex].activeChallenge === sentData.challengeIndex) {
                this.expeditionMainState.teamStates[sentData.teamIndex].activeChallenge = null;
                this.expeditionMainState.teamStates[sentData.teamIndex].endedChallenges.push({
                    challengeIndex: sentData.challengeIndex,
                    startedTime: this.expeditionMainState.teamStates[sentData.teamIndex].activeChallengeStartTime,
                    completedTime: sentData.completedTime,
                    successful: sentData?.successful,
                });
                this.expeditionMainState.teamStates[sentData.teamIndex].activeChallengeStartTime = null;
                this.expeditionMainState.teamStates[sentData.teamIndex].totalXp += sentData.xpAwarded;
            }
        }
        this.informUpdateListenersNow(data);
    }

    onUpdateTeamLocation = (data) => {
        // data = {teamIndex, lat, long}
        console.log('--- ExpeditionLiveState, onUpdateTeamLocation', data);
        const sentData = data?.data ?? data;
        // Update local state immediately
        if (this.expeditionMainState && this.expeditionMainState.teamStates && this.expeditionMainState.teamStates[sentData.teamIndex]) {
            this.expeditionMainState.teamStates[sentData.teamIndex].stagePercentage = sentData.stagePercentage;
            this.expeditionMainState.teamStates[sentData.teamIndex].latitude = sentData.latitude;
            this.expeditionMainState.teamStates[sentData.teamIndex].longitude = sentData.longitude;
        }
        this.informUpdateListenersNow(data);
    }

    delayedInformListeners = (delay = 1000) => {
        setTimeout(() => {
            this.informUpdateListenersNow({ type: 'delayedInformListeners', data: this.expeditionMainState });
        }, delay);
    }

    sendDashboardCommand = (command, data) => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, sendDashboardCommand:', command, data);
            this.socket.emit('expeditionDashboardSendCommand', { command: command, ...data });
        }
    }

    sendMessageToTeam = (teamIndex, message) => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, sendMessageToTeam:', teamIndex, message);
            // this.socket.emit('expeditionDashboardSendCommand', { command: 'sendMessageToTeam', data: { teamIndex: teamIndex, message: message } });
            this.sendDashboardCommand('sendMessageToTeam', { teamIndex: teamIndex, message: message });
        }
    }

    unlockChatForTeam = (duration, teamIndex) => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, unlockChatForTeam:', teamIndex, duration);
            this.sendDashboardCommand('unlockTeamChat', { teamIndex: teamIndex, duration: duration, unlockUntil: Math.floor(Date.now() / 1000) + duration });
            // Also update local state immediately
            if (this.expeditionMainState && this.expeditionMainState.teamStates && this.expeditionMainState.teamStates[teamIndex]) {
                this.expeditionMainState.teamStates[teamIndex].chatUnlockedFor = duration;
                this.expeditionMainState.teamStates[teamIndex].chatUnlockedUntil = Math.floor(Date.now() / 1000) + duration;
            }
            this.delayedInformListeners();
        }
    }

    unlockChatForAllTeams = (duration) => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, unlockChatForAllTeams:', duration);
            this.sendDashboardCommand('unlockAllChat', { duration: duration, unlockUntil: Math.floor(Date.now() / 1000) + duration });
            // Also update local state immediately
            if (this.expeditionMainState) {
                this.expeditionMainState.allTeamsChatUnlocked = true;
                this.expeditionMainState.allTeamsChatUnlockedFor = duration;
                this.expeditionMainState.allTeamsChatUnlockedUntil = Math.floor(Date.now() / 1000) + duration;
            }
            this.delayedInformListeners();
        }
    }

    freezeComapassForTeam = (duration, teamIndex) => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, freezeCompassForTeam:', teamIndex, duration);
            this.sendDashboardCommand('lockTeamCompass', { teamIndex: teamIndex, duration: duration, lockUntil: Math.floor(Date.now() / 1000) + duration });
            // Also update local state immediately
            if (this.expeditionMainState && this.expeditionMainState.teamStates && this.expeditionMainState.teamStates[teamIndex]) {
                this.expeditionMainState.teamStates[teamIndex].compassLockedFor = duration;
                this.expeditionMainState.teamStates[teamIndex].compassLockedUntil = Math.floor(Date.now() / 1000) + duration;
            }
            this.delayedInformListeners();
        }
    }

    silentlySetJourneyStageForTeam = (teamIndex, stageIndex, waypointIndex, challengeIndex) => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, silentlySetJourneyStageForTeam:', teamIndex, stageIndex, waypointIndex, challengeIndex);
            this.sendDashboardCommand('silentlySetJourneyStage', { teamIndex: teamIndex, stageIndex: stageIndex, waypointIndex: waypointIndex, challengeIndex: challengeIndex });
            // Also update local state immediately
            if (this.expeditionMainState && this.expeditionMainState.teamStates && this.expeditionMainState.teamStates[teamIndex]) {
                this.expeditionMainState.teamStates[teamIndex].journeyStage = stageIndex;
                this.expeditionMainState.teamStates[teamIndex].currentWaypoint = waypointIndex;
                this.expeditionMainState.teamStates[teamIndex].stagePercentage = 0;
                if (this.updateListeners.length > 0) {
                    this.updateListeners.forEach((callback) => {
                        if (callback) callback(this.expeditionMainState, { type: 'silentlySetJourneyStage', data: { teamIndex: teamIndex, stageIndex: stageIndex, waypointIndex: waypointIndex, challengeIndex: challengeIndex } });
                    });
                }
            }
        }
    }

    waypointReachedForTeam = (teamIndex, stageIndex, waypointIndex) => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, waypointReachedForTeam:', teamIndex, stageIndex, waypointIndex);
            this.sendDashboardCommand('waypointReached', { teamIndex: teamIndex, stageIndex: stageIndex, waypointIndex: waypointIndex });
            // Also update local state immediately
            if (this.expeditionMainState && this.expeditionMainState.teamStates && this.expeditionMainState.teamStates[teamIndex]) {
                this.expeditionMainState.teamStates[teamIndex].journeyStage = stageIndex;
                this.expeditionMainState.teamStates[teamIndex].currentWaypoint = waypointIndex;
                if (this.updateListeners.length > 0) {
                    this.updateListeners.forEach((callback) => {
                        if (callback) callback(this.expeditionMainState, { type: 'waypointReached', data: { teamIndex: teamIndex, stageIndex: stageIndex, waypointIndex: waypointIndex } });
                    });
                }
            }
        }
    }
    checkpointReachedForTeam = (teamIndex, stageIndex, waypointIndex, challengeIndex) => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, checkpointReachedForTeam:', teamIndex, stageIndex, waypointIndex, challengeIndex);
            this.sendDashboardCommand('checkpointReached', { teamIndex: teamIndex, stageIndex: stageIndex, waypointIndex: waypointIndex, challengeIndex: challengeIndex });
            // Also update local state immediately
            if (this.expeditionMainState && this.expeditionMainState.teamStates && this.expeditionMainState.teamStates[teamIndex]) {
                this.expeditionMainState.teamStates[teamIndex].journeyStage = stageIndex;
                this.expeditionMainState.teamStates[teamIndex].currentWaypoint = waypointIndex;
                this.expeditionMainState.teamStates[teamIndex].activeChallenge = challengeIndex;
                this.expeditionMainState.teamStates[teamIndex].activeChallengeStartTime = 0;
                if (this.updateListeners.length > 0) {
                    this.updateListeners.forEach((callback) => {
                        if (callback) callback(this.expeditionMainState, { type: 'checkpointReached', data: { teamIndex: teamIndex, stageIndex: stageIndex, waypointIndex: waypointIndex, challengeIndex: challengeIndex } });
                    });
                }
            }
        }
    }

    startChallengeForTeam = (teamIndex, challengeIndex) => {
        if (this.socket /* && this.expeditionMainState.teamStates[teamIndex].activeChallenge === null */) {
            console.log('--- ExpeditionLiveState, startChallengeForTeam:', teamIndex, challengeIndex);
            const startTime = Math.floor(Date.now() / 1000);
            this.sendDashboardCommand('challengeStarted', { teamIndex: teamIndex, challengeIndex: challengeIndex, startTime: startTime });
            // Also update local state immediately
            console.log('--- ExpeditionLiveState, startChallengeForTeam, updating local state:', teamIndex, challengeIndex, startTime, this.expeditionMainState);
            if (this.expeditionMainState && this.expeditionMainState.teamStates && this.expeditionMainState.teamStates[teamIndex]) {
                console.log('--- ExpeditionLiveState, startChallengeForTeam, updating local state 2:', teamIndex, challengeIndex, startTime, this.updateListeners);
                this.expeditionMainState.teamStates[teamIndex].activeChallenge = challengeIndex;
                this.expeditionMainState.teamStates[teamIndex].activeChallengeStartTime = startTime;
                if (this.updateListeners.length > 0) {
                    console.log('--- ExpeditionLiveState, startChallengeForTeam, notifying listeners:', teamIndex, challengeIndex, startTime);
                    this.updateListeners.forEach((callback) => {
                        if (callback) callback(this.expeditionMainState, { type: 'startChallengeForTeam', data: { teamIndex: teamIndex, challengeIndex: challengeIndex, startTime: startTime } });
                    });
                }
            }
        }
    }
    challengeCompletedForTeam = (teamIndex, challengeIndex, successful, moveOnToJourneyStage, isFinalChallenge, xpAwarded) => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, challengeCompletedForTeam:', teamIndex, challengeIndex, successful, moveOnToJourneyStage, isFinalChallenge, xpAwarded);
            const completedTime = Math.floor(Date.now() / 1000);
            this.sendDashboardCommand('challengeComplete', { teamIndex: teamIndex, challengeIndex: challengeIndex, completedTime: completedTime, successful: successful, moveOnToJourneyStage: moveOnToJourneyStage, isFinalChallenge: isFinalChallenge, xpAwarded: xpAwarded });
            // Also update local state immediately
            if (this.expeditionMainState && this.expeditionMainState.teamStates && this.expeditionMainState.teamStates[teamIndex]) {
                if (this.expeditionMainState.teamStates[teamIndex].activeChallenge === challengeIndex) {
                    const completedChallenge = {
                        challengeIndex: challengeIndex,
                        startedTime: this.expeditionMainState.teamStates[teamIndex].activeChallenge?.startedTime || this.expeditionMainState.teamStates[teamIndex].activeChallengeStartTime || 0,
                        completedTime: completedTime,
                        successful: successful,
                    };
                    this.expeditionMainState.teamStates[teamIndex].endedChallenges.push(completedChallenge);
                    this.expeditionMainState.teamStates[teamIndex].activeChallenge = null;
                    this.expeditionMainState.teamStates[teamIndex].totalXp += xpAwarded || 0;
                    if (!isFinalChallenge && moveOnToJourneyStage !== undefined && moveOnToJourneyStage !== null) {
                        this.expeditionMainState.teamStates[teamIndex].journeyStage = moveOnToJourneyStage;
                        this.expeditionMainState.teamStates[teamIndex].currentWaypoint = 0;
                    }
                    if (isFinalChallenge) {
                        this.expeditionMainState.teamStates[teamIndex].expeditionCompleted = true;
                        this.expeditionMainState.teamStates[teamIndex].expeditionCompletedTime = completedTime;
                    }
                    if (this.updateListeners.length > 0) {
                        this.updateListeners.forEach((callback) => {
                            if (callback) callback(this.expeditionMainState, { type: 'challengeCompletedForTeam', data: { teamIndex: teamIndex, challengeIndex: challengeIndex, completedTime: completedTime, successful: successful, moveOnToJourneyStage: moveOnToJourneyStage, isFinalChallenge: isFinalChallenge, xpAwarded: xpAwarded } });
                        });
                    }
                }
            }
        }
    }

    getActiveExpedition = async () => {
        try {
            const response = await axios.get(getActiveExpeditionRoute);
            if (response && response.data) {
                this.expeditionMainState = response.data.activeExpedition;
                return response.data.activeExpedition;
            }
        } catch (error) {
            console.error("Error fetching active expedition:", error);
            return null;
        }
    }

    activateNewExpedition = async (campaignID, streamID, featureID, assetBaseUrl, defaultAssetBaseUrl, customisationData, completedCallback) => {
        const expeditionData = getInitialExpeditionState(customisationData, campaignID, featureID, assetBaseUrl, defaultAssetBaseUrl);
        try {
            const response = await axios.post(activateExpeditionRoute, {
                campaignID,
                streamID,
                expeditionData
            });
            if (response && response.status === 200) {
                this.expeditionMainState = expeditionData;
                if (completedCallback) completedCallback();
                this.delayedInformListeners();
                return expeditionData;
            }
        } catch (error) {
            console.error("Error activating new expedition:", error);
            return null;
        }
    }
    activateRecoveredExpedition = async (campaignID, streamID, recoveredExpeditionData, completedCallback) => {
        const expeditionData = recoveredExpeditionData;
        try {
            const response = await axios.post(activateExpeditionRoute, {
                campaignID,
                streamID,
                expeditionData
            });
            if (response && response.status === 200) {
                this.expeditionMainState = expeditionData;
                if (completedCallback) completedCallback();
                this.delayedInformListeners();
                return expeditionData;
            }
        } catch (error) {
            console.error("Error activating new expedition:", error);
            return null;
        }
    }
    recoverExpeditionData = async (campaignID, completedCallback) => {
        try {
            const response = await axios.post(recoverExpeditionRoute, {
                campaignID,
            });
            if (response && response.status === 200) {
                console.log("Recovered expedition data:", response.data);
                if (completedCallback) completedCallback(response.data.expeditionData);
                return response.data;
            } else {
                if (completedCallback) completedCallback(null);
                return null;
            }
        } catch (error) {
            console.error("Error recovering expedition:", error);
            if (completedCallback) completedCallback(null);
            return null;
        }
    }

    startExpedition = async (timeAllowed, completedCallback) => {
        if (!this.expeditionMainState || !this.expeditionMainState.expeditionInited || this.expeditionMainState.expeditionActive) {
            console.error("Cannot start expedition: Expedition not initialised or already active.");
            return false;
        }
        const currentTime = Math.floor(Date.now() / 1000);
        this.expeditionMainState.expeditionActive = true;
        this.expeditionMainState.expeditionStartTime = currentTime;
        this.expeditionMainState.expeditionTimeAllowed = timeAllowed;
        this.expeditionMainState.expeditionTargetEndTime = currentTime + timeAllowed;
        // Send command to backend to start the expedition
        const response = await axios.post(startExpeditionRoute, { timeStarted: currentTime, timeAllowed: timeAllowed });
        if (completedCallback) completedCallback();
        this.delayedInformListeners();
    }

    deactivateExpedition = async (completedCallback) => {
        try {
            const response = await axios.post(deactivateExpeditionRoute);
            if (response && response.status === 200) {
                this.expeditionMainState = null;
                this.cancelSocketListeners();
                if (completedCallback) completedCallback();
                this.delayedInformListeners();
                return true;
            }
        } catch (error) {
            console.error("Error deactivating expedition:", error);
            if (completedCallback) completedCallback();
            this.delayedInformListeners();
            return false;
        }
    }

    adminForceHeartbeat = () => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, adminForceHeartbeat');
            this.socket.emit('expeditionDashboardSendCommand', { command: 'admin_forceHeartbeat' });
        }
    }
    adminForceTeamUpdate = (teamIndex) => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, adminForceTeamUpdate');
            this.socket.emit('expeditionDashboardSendCommand', { command: 'admin_forceTeamUpdateFromServer', data: { teamIndex: teamIndex } });
        }
    }
    adminSetEntireExpeditionState = (newState) => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, adminSetExpeditionState');
            this.socket.emit('expeditionDashboardSendCommand', { command: 'admin_setExpeditionState', data: { newStateData: newState } });
        }
    }
    adminUpdateTeamXp = (teamIndex, newXp) => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, adminUpdateTeamXp');
            this.socket.emit('expeditionDashboardSendCommand', { command: 'admin_updateTeamXp', data: { teamIndex: teamIndex, newXp: newXp } });
        }
    }
    adminLockAllChat = () => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, adminLockAllChat');
            this.socket.emit('expeditionDashboardSendCommand', { command: 'admin_lockAllChat' });
        }
    }
    adminLockTeamChat = (teamIndex) => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, adminLockTeamChat');
            this.socket.emit('expeditionDashboardSendCommand', { command: 'admin_lockTeamChat', data: { teamIndex: teamIndex } });
        }
    }
    adminUnlockTeamCompass = (teamIndex) => {
        if (this.socket) {
            console.log('--- ExpeditionLiveState, adminUnlockTeamCompass');
            this.socket.emit('expeditionDashboardSendCommand', { command: 'admin_unlockTeamCompass', data: { teamIndex: teamIndex } });
        }
    }
}

const EXPEDITION_LIVE_STATE = new ExpeditionLiveState();
export default EXPEDITION_LIVE_STATE;
